import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import AdminLayout from "@/components/admin/AdminLayout";
import RejectReasonModal from "@/components/admin/RejectReasonModal";
import AdminReviewEmpty from "@/components/admin/review/AdminReviewEmpty";
import AdminReviewFilters from "@/components/admin/review/AdminReviewFilters";
import AdminReviewGrid from "@/components/admin/review/AdminReviewGrid";
import AdminReviewList from "@/components/admin/review/AdminReviewList";
import AdminReviewPagination from "@/components/admin/review/AdminReviewPagination";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth";
import { useApproveAdminListing, useRejectAdminListing } from "@/hooks/admin/useAdminListingActions";
import { useModerationQueue } from "@/hooks/admin/useModerationQueue";
import { usePermissions } from "@/services/permissionHelper";
import type { ModerationQueueItem } from "@/types/api";

type StatusFilter = "all" | "pending" | "approved" | "rejected" | "unassigned";

const AdminReviewPage = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [providerToReject, setProviderToReject] = useState<ModerationQueueItem | null>(null);
  const itemsPerPage = viewMode === "list" ? 10 : 9;
  const navigate = useNavigate();
  const approveListing = useApproveAdminListing();
  const rejectListing = useRejectAdminListing();
  const isProcessing = approveListing.isPending || rejectListing.isPending;

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm, viewMode]);

  const { data: providers = [], isLoading, error } = useModerationQueue("all", !!user);

  const statusCounts = useMemo(
    () => ({
      all: providers.length,
      pending: providers.filter((provider) => provider.status === "pending").length,
      approved: providers.filter((provider) => provider.status === "approved").length,
      rejected: providers.filter((provider) => provider.status === "rejected").length,
      unassigned: providers.filter((provider) => provider.flags.isUnassigned).length,
    }),
    [providers],
  );

  const filteredProviders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return providers
      .filter((provider) => {
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "unassigned" ? provider.flags.isUnassigned : provider.status === statusFilter);

        if (!matchesStatus) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const haystack = [
          provider.name,
          provider.description,
          provider.owner.email,
          provider.owner.firstName,
          provider.owner.lastName,
          provider.profiles?.email,
          ...(provider.categories || [provider.category]),
          ...(provider.suburbs || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      })
      .sort((left, right) => {
        const leftDate = new Date(left.submittedAt).getTime();
        const rightDate = new Date(right.submittedAt).getTime();
        return rightDate - leftDate;
      });
  }, [providers, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
  const paginatedProviders = filteredProviders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleStatusChange = async (
    listingId: string,
    status: "approved" | "rejected",
    rejectionReason?: string,
  ) => {
    try {
      if (status === "approved") {
        await approveListing.mutateAsync({ listingId });
      } else {
        await rejectListing.mutateAsync({
          listingId,
          reason: rejectionReason || "Rejected by admin review",
        });
      }

      toast.success(`Listing ${status === "approved" ? "approved" : "rejected"}`);
    } catch (actionError) {
      console.error(`Error updating listing status:`, actionError);
      toast.error(`Failed to ${status} listing`);
    }
  };

  const handleRejectClick = (listingId: string) => {
    const provider = providers.find((item) => item.listingId === listingId);
    if (provider) {
      setProviderToReject(provider);
      setRejectModalOpen(true);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (providerToReject) {
      await handleStatusChange(providerToReject.listingId, "rejected", reason);
      setRejectModalOpen(false);
      setProviderToReject(null);
    }
  };

  if (!isAdmin && user) {
    return (
      <AdminLayout title="Review Listings">
        <AdminReviewEmpty
          searchTerm=""
          statusFilter="pending"
          setSearchTerm={() => undefined}
          setStatusFilter={() => undefined}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Review Listings"
      description="The moderation queue is optimized for fast scanning: visible filters first, actionable status second, and direct approval or rejection inline."
      actions={
        <Button
          asChild
          size="sm"
          className="rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
        >
          <Link to="/admin/create-listing">Create listing</Link>
        </Button>
      }
    >
      <div className="space-y-6">
        <AdminReviewFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          statusCounts={statusCounts}
          filteredCount={filteredProviders.length}
          totalCount={providers.length}
          onReset={() => {
            setSearchTerm("");
            setStatusFilter("pending");
            setViewMode("list");
          }}
        />

        {isLoading ? (
          <div className="grid gap-4 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-56 animate-pulse rounded-lg border border-gray-200 bg-white shadow-sm" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-5 text-red-700">
            <p className="font-medium">The moderation queue could not be loaded.</p>
            <p className="mt-1 text-sm text-red-600">{error.message}</p>
          </div>
        ) : filteredProviders.length === 0 ? (
          <AdminReviewEmpty
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            setSearchTerm={setSearchTerm}
            setStatusFilter={setStatusFilter}
          />
        ) : (
          <>
            {viewMode === "grid" ? (
              <AdminReviewGrid
                providers={paginatedProviders}
                isProcessing={isProcessing}
                handleEditListing={(listingId) => navigate(`/admin/edit-listing/${listingId}`)}
                handleStatusChange={(listingId, status) =>
                  status === "rejected"
                    ? handleRejectClick(listingId)
                    : handleStatusChange(listingId, status)
                }
              />
            ) : (
              <AdminReviewList
                providers={paginatedProviders}
                isProcessing={isProcessing}
                handleEditListing={(listingId) => navigate(`/admin/edit-listing/${listingId}`)}
                handleStatusChange={(listingId, status) =>
                  status === "rejected"
                    ? handleRejectClick(listingId)
                    : handleStatusChange(listingId, status)
                }
              />
            )}

            <AdminReviewPagination
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          </>
        )}

        <RejectReasonModal
          isOpen={rejectModalOpen}
          onClose={() => {
            setRejectModalOpen(false);
            setProviderToReject(null);
          }}
          onConfirm={handleRejectConfirm}
          providerName={providerToReject?.name || ""}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminReviewPage;
